/**
 * The shape flyout. §6.4.
 *
 * Every shape is drawn from the same library the canvas and the press file use,
 * so a preview here is the shape that will actually print - not an icon of it
 * that somebody has to remember to redraw when the shape changes.
 *
 * It opens beside the rail rather than taking over the properties panel,
 * because the customer is usually looking at the card while deciding where the
 * thing should go.
 */

import { useEffect, useRef } from 'react';
import { shapeGroups, type ShapeDef } from '../shapes/library';
import type { ShapeKind } from '../model/types';

interface Props {
  onPick: ( shape: ShapeKind ) => void;
  onClose: () => void;
}

/** The box every preview is drawn in. Square, so nothing is favoured. */
const TILE = 34;

export function ShapePicker( { onPick, onClose }: Props ) {
  const panel = useRef<HTMLDivElement>( null );

  // Escape closes, and so does clicking anywhere else - the two ways out of
  // every other transient panel in this editor.
  useEffect( () => {
    const key = ( event: KeyboardEvent ) => {
      if ( event.key === 'Escape' ) onClose();
    };

    const away = ( event: MouseEvent ) => {
      const target = event.target as Node;

      if ( panel.current && ! panel.current.contains( target ) && ! inRail( target ) ) onClose();
    };

    window.addEventListener( 'keydown', key );
    // Late, so the click that opened this does not immediately close it.
    const timer = window.setTimeout( () => window.addEventListener( 'mousedown', away ), 0 );

    return () => {
      window.removeEventListener( 'keydown', key );
      window.removeEventListener( 'mousedown', away );
      window.clearTimeout( timer );
    };
  }, [ onClose ] );

  return (
    <div className="tb-shapes" ref={ panel } role="dialog" aria-label="Shapes">
      <div className="tb-shapes__scroll">
        { shapeGroups().map( ( { group, shapes } ) => (
          <section className="tb-shapes__group" key={ group }>
            <h2 className="tb-shapes__heading">{ group }</h2>

            <div className="tb-shapes__grid">
              { shapes.map( ( shape ) => (
                <button
                  type="button"
                  key={ shape.id }
                  className="tb-shapes__tile"
                  title={ shape.name }
                  onClick={ () => onPick( shape.id ) }
                >
                  <Preview shape={ shape } />
                  <span className="tb-shapes__name">{ shape.name }</span>
                </button>
              ) ) }
            </div>
          </section>
        ) ) }
      </div>
    </div>
  );
}

/**
 * One shape, at tile size.
 *
 * The library is asked for a path in tile units, which is the same call the
 * canvas makes in pixels and the press walk makes in millimetres.
 */
export function Preview( { shape }: { shape: ShapeDef } ) {
  // A little inset, so a shape that fills its box does not touch the tile edge.
  const pad = 3;
  const size = TILE - pad * 2;

  // Drawn in the proportion the shape will actually be placed at, centred in
  // the square tile. A ribbon squeezed into a square is unrecognisable.
  const ratio = shape.ratio ?? 1;
  const w = ratio >= 1 ? size : size * ratio;
  const h = ratio >= 1 ? size / ratio : size;

  const d = shape.path( w, h, {
    radius: shape.id === 'rect' ? 4 : Math.min( w, h ) * 0.18,
    points: shape.id === 'polygon' ? 6 : shape.id === 'burst' ? 12 : shape.id === 'seal' ? 12 : 5,
  } );

  return (
    <svg className="tb-shapes__preview" viewBox={ `0 0 ${ TILE } ${ TILE }` } aria-hidden="true">
      <g transform={ `translate(${ pad + ( size - w ) / 2 } ${ pad + ( size - h ) / 2 })` }>
        <path
          d={ d }
          fill={ shape.strokeOnly ? 'none' : 'currentColor' }
          stroke="currentColor"
          strokeWidth={ shape.strokeOnly ? 2 : 0 }
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

/** Clicks on the rail are the rail's business, including the one that closes this. */
function inRail( node: Node ): boolean {
  return !! ( node instanceof Element ? node.closest( '.tb-rail' ) : null );
}
